import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { confirmResetPassword } from 'aws-amplify/auth';
import Notification from './Notification';
import Spinner from './Spinner';
import '../styles/Auth.css';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    email: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-fill email if passed from forgot password page
  React.useEffect(() => {
    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }
  }, [location.state]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setNotification({
        show: true,
        message: 'Passwords do not match',
        type: 'error'
      });
      return;
    }

    if (formData.newPassword.length < 8) {
      setNotification({
        show: true,
        message: 'Password must be at least 8 characters long',
        type: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      await confirmResetPassword({
        username: formData.email,
        confirmationCode: formData.code,
        newPassword: formData.newPassword
      });
      
      setNotification({
        show: true,
        message: 'Password reset successful! Redirecting to login...',
        type: 'success'
      });
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      console.error('Reset password error:', error);
      setNotification({
        show: true,
        message: error.message || 'Failed to reset password',
        type: 'error'
      });
    } finally {
      setLoading(false);
      setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
    }
  };

  return (
    <div className="auth-container">
      {notification.show && (
        <Notification 
          message={notification.message} 
          type={notification.type} 
        />
      )}
      
      <div className="auth-card">
        <h2>Reset Password</h2>
        <p className="auth-description">
          Enter the verification code sent to your email and your new password.
        </p>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-control"
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="code">Verification Code</label>
            <input
              type="text"
              id="code"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
              className="form-control"
              placeholder="Enter the code from your email"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              required
              className="form-control"
              placeholder="Enter new password"
              minLength="8"
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              className="form-control"
              placeholder="Confirm new password"
              minLength="8"
            />
          </div>
          
          <button 
            type="submit" 
            className="btn-primary auth-btn"
            disabled={loading}
          >
            {loading ? <Spinner size="small" message="" /> : 'Reset Password'}
          </button>
        </form>
        
        <div className="auth-links">
          <p className="auth-link">
            Remember your password? <Link to="/login">Back to Login</Link>
          </p>
          <p className="auth-link">
            Need to resend code? <Link to="/forgot-password">Resend Reset Email</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;